import { type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Blaze, Box, Card, Paper, StackColumn } from '@dhl-ui/components'
import {
  LockerOpenConfettiIllustration,
  ParcelType404Illustration,
  ServicePointConfettiIllustration,
} from '@dhl-ui/illustrations'
import { type Servicepoint, ServicepointDetailsCard } from '@dhl/receiver-servicepoint'

import { type ServicepointTypeKey } from '../../servicepoints/hooks/useGetServicepointDetails.js'
import { NewServicepointInAreaContentSkeleton } from './NewServicepointInAreaContentSkeleton.js'

type Props = {
  isLoading: boolean
  typeKey: ServicepointTypeKey
  servicepoint?: Servicepoint
}

export const NewServicepointInAreaContent: FC<Props> = ({ isLoading, servicepoint, typeKey }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <NewServicepointInAreaContentSkeleton />
  }

  if (!servicepoint) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 4rem)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Blaze
          description={t(`portal.servicepoints.${typeKey}.notFound`)}
          slotProps={{ illustration: { variant: ParcelType404Illustration } }}
          sx={{
            m: '0 auto',
          }}
        />
      </Box>
    )
  }

  return (
    <Card hasPaddingBottom>
      <Card.Content>
        <StackColumn gap={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Blaze
              slotProps={{
                illustration: {
                  variant: servicepoint?.isLocker ? LockerOpenConfettiIllustration : ServicePointConfettiIllustration,
                },
              }}
              description={t(`portal.servicepoints.${typeKey}.newDescription`)}
              sx={{ alignSelf: 'center' }}
            />
          </Box>

          <Paper background="dark">
            <ServicepointDetailsCard showIcon servicepoint={servicepoint} />
          </Paper>
        </StackColumn>
      </Card.Content>
    </Card>
  )
}
